import {useRouter} from 'next/router';
import {useEffect} from 'react';
import {requestContext} from '../../../../../../common/requestContext';
import {INIPlayer} from '../../../../../../common/shared/ini-player/INIPlayer.interface';
import useTypedDispatch from '../../../../../../common/useTypedDispatch';
import useTypedSelector from '../../../../../../common/useTypedSelector';
import VideoPlayerControlBox from '../../../../../../design-system/organisms/VideoPlayerControlBox/VideoPlayerControlBox';
import {putTiaraObjectProps} from '../../../../../../logging/putTiaraObjectProps';
import {VideoStatus} from '../constant/videoViewerConstant';
import {useVideoViewerControl} from '../hook/useVideoViewerControl';
import {setIsEpisodeChanging} from '../state/viewerInfoSlice';

export interface VideoPlayerControlBoxContainerProps {
  videoRef: React.RefObject<INIPlayer>;
  productId: number;
  duration: number;
  currentTime: number;
  loadedProgress: number;
  isShortsDrama?: boolean;
  hasNextEpisode?: boolean;
  setTimeUpdateHandlerEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  setCurrentTime: React.Dispatch<React.SetStateAction<number>>;
}

export function VideoPlayerControlBoxContainer({
  videoRef,
  currentTime,
  duration,
  loadedProgress,
  setTimeUpdateHandlerEnabled,
  setCurrentTime,
  isShortsDrama,
  hasNextEpisode,
}: VideoPlayerControlBoxContainerProps) {
  const {
    videoState,
    showControlBox,
    fullscreenEnabled,
    ticketFlowInProgress,
    isEpisodeChanging,
  } = useTypedSelector(state => ({
    videoState: state.videoViewer.videoState,
    showControlBox: state.videoViewer.showControlBox,
    fullscreenEnabled: state.videoViewer.fullscreenEnabled,
    ticketFlowInProgress: state.viewerInfo.ticketFlowInProgress,
    isEpisodeChanging: state.viewerInfo.isEpisodeChanging,
  }));
  const isMobile = requestContext.requestInfo.display.isMobile;
  const isLoading = videoState.isLoading;
  const dispatch = useTypedDispatch();

  const router = useRouter();

  /**
   * 쇼츠드라마 정주행모드를 통한 다음회차 이동시에 뷰어 컨트롤러 미노출이 필요하다
   * 현재 회차 -> 다음 회차 이동을 ticketFlowInProgress를 이용하여 미노출 할 수 있다.
   * 하지만 현재회차 -> 이용권 플로우 -> 다음회차 진입 및 이용권 플로우 에서 이용권 플로우 사이에 ticketFlowInProgress가 false가 되어 컨트롤박스가 잠깐 노출되고 있다.
   * isEpisodeChanging을 사용하여 이전회차 -> 다음회차 진입 까지의 플래그 값을 routeChange로 관리 하여 컨트롤박스 미노출을 유지한다.
   */
  useEffect(() => {
    if (!isShortsDrama) {
      return;
    }
    const handleRouteChangeStart = () => {
      dispatch(setIsEpisodeChanging(true));
    };
    const handleRouteChangeComplete = () => {
      dispatch(setIsEpisodeChanging(false));
    };
    router.events.on('routeChangeStart', handleRouteChangeStart);
    router.events.on('routeChangeComplete', handleRouteChangeComplete);
    return () => {
      dispatch(setIsEpisodeChanging(false));
      router.events.off('routeChangeStart', handleRouteChangeStart);
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
    };
  }, [isShortsDrama, dispatch, router]);

  const {
    onClickMuteBtn,
    onClickPlayBtn,
    onClickInitialPlayBtn,
    onClickPauseBtn,
    onClickReplayBtn,
    onClickFullscreenBtn,
    onChangeProgress,
    onPressProgressBar,
    onReleaseProgressBar,
    onClickPrevMoveBtn,
    onClickNextMoveBtn,
    onClickTogglePlayback,
    onChangeVolume,
    isSeeking,
  } = useVideoViewerControl({
    videoRef,
    duration,
    setTimeUpdateHandlerEnabled,
    setCurrentTime,
    isShortsDrama,
    hasNextEpisode,
  });

  return (
    <VideoPlayerControlBox
      isMobile={isMobile}
      videoStatus={videoState.videoStatus}
      isLoading={isLoading}
      isSeeking={isSeeking}
      isMute={videoState.isMute || videoState.volume === 0}
      volume={videoState.isMute ? 0 : videoState.volume}
      currentTime={currentTime}
      loadedProgress={loadedProgress}
      duration={duration}
      onClickMuteBtn={onClickMuteBtn}
      onChangeVolume={onChangeVolume}
      onClickPlayBtn={
        videoState.videoStatus === VideoStatus.BeforeInitialPlay
          ? onClickInitialPlayBtn
          : onClickPlayBtn
      }
      onClickPauseBtn={onClickPauseBtn}
      onClickReplayBtn={onClickReplayBtn}
      onClickFullscreenBtn={
        fullscreenEnabled ? onClickFullscreenBtn : undefined
      }
      onClickPrevMoveBtn={onClickPrevMoveBtn}
      onClickNextMoveBtn={onClickNextMoveBtn}
      onClickTogglePlayback={onClickTogglePlayback}
      onChangeProgress={onChangeProgress}
      onPressProgressBar={onPressProgressBar}
      onReleaseProgressBar={onReleaseProgressBar}
      isShortsDrama={isShortsDrama}
      ticketFlowInProgress={ticketFlowInProgress}
      hasNextEpisode={hasNextEpisode}
      isEpisodeChanging={isEpisodeChanging}
      className={
        !showControlBox && (!isShortsDrama || (isShortsDrama && !isMobile))
          ? 'hidden'
          : undefined
      }
      {...putTiaraObjectProps({
        click: {
          layer1: '뷰어메뉴',
        },
        customProps: {
          is_shortdrama: isShortsDrama ? 'y' : undefined,
        },
      })}
    />
  );
}
